// HI1 — **THE TWO ICONS IN THE TOP MENU, AND THE ONE THAT GLOWS.** Her order, 2026-08-04:
// the mail and the bell get BIGGER (comfortably tappable, visually present, not the small glyphs
// they were), and the MESSAGE icon carries a LIGHT AQUA GLOW behind it. Badges still render on both.
//
// **THIS FILE IS THE LAW BECAUSE THE NUMBERS CROSS A BOUNDARY THAT NOTHING WATCHED.** The glyph size
// is a prop in TSX and the tap target is a rule in `styles.css`, and until this round they were two
// hand-typed numbers with a stylesheet between them: a glyph grown past its button overflows a
// 999px-radius pill, and a button shrunk under the tap floor is a control she misses on a phone.
// R13/R14/R15's ruling — a fixture holding a cross-boundary law must open BOTH sides — so the numbers
// are declared once here and `scripts/hi1-header-icons.mjs` holds the stylesheet against them.
//
// It imports NOTHING, which is the entry requirement that lets a `.mjs` gate load and RUN it.

// THE ROSTER. Two kinds, and every table below is held against it BOTH DIRECTIONS (R28/R29), so a
// third header icon joins by EXISTING rather than by somebody remembering four places.
export const HEADER_ICON_KINDS = ["notifications", "messages"];

// THE GEOMETRY, IN ONE PLACE.
//   `button`   — the tap target, and it is what `.house-inbox__btn`'s min-width/min-height must be.
//   `glyph`    — the lucide `size` both icons are drawn at. It was 17 against a 44px button, which is
//                what "the current small glyphs" names: a third of the control was ink.
//   `tapFloor` — the standing 44px floor this house holds every control to (P4-5 measured it, and
//                measured that an emulated viewport is not the place to measure it). The button may
//                never go under it; it may go over it, which is exactly what this round does.
//   `clearance` — the glyph must leave at least this much of the button around it, so the ink never
//                reaches the pill's own border.
export const HEADER_ICON = { button: 48, glyph: 22, tapFloor: 44, clearance: 8 };

// **THE GLOW IS ONE KIND'S, NOT A STYLE ANYBODY MAY REACH FOR.** Her ruling names the MESSAGE icon
// and only it — the glow is what draws the eye to the door she wants drawn to, and a glow on both is
// a glow on neither. Declared as a value rather than as a `kind === "messages"` at the render site,
// because that spelling is what R28/R29 is about: the day a third icon lands, the question is asked
// of this file instead of being re-decided in JSX.
export const GLOWING_HEADER_ICON = "messages";

// An unrostered kind never glows. It is not "not the glowing one" by luck — it is refused, because a
// glow is a claim about which door matters (R141: refuse, never assume).
export function headerIconGlows(kind) {
  if (!HEADER_ICON_KINDS.includes(kind)) return false;
  return kind === GLOWING_HEADER_ICON;
}

// THE BADGE TEXT, WHICH WAS TYPED TWICE. `unread > 9 ? "9+" : unread` stood on both buttons, three
// lines apart, which is R28/R29's disease at its smallest — and the two copies is exactly how a
// count comes to render as "10" on one badge and "9+" on the other.
//
// The EMPTY STRING is "draw no badge", and it is a THIRD answer rather than a falsy count (R18):
// a count that has not arrived and a count of zero both mean nothing to draw HERE, but the caller
// keeps them apart (R84: a read that did not land leaves the last count she was told standing).
// A non-number, a negative and a fraction all draw nothing — `Number(true)` is 1 and row one belongs
// to somebody (R136/R137), so only a real whole count above zero is a badge.
// **HER RULING: THE REAL NUMBER, UP TO THREE DIGITS.** It capped at 9, so a member with 12 waiting
// and a member with 357 waiting saw the identical badge — the count stopped being information at the
// exact point it started mattering. 9 is 9, 12 is 12, 357 is 357, and the cap moves to where three
// digits run out. The ceiling is DERIVED from the digit count rather than typed as 999, so the two
// halves of "three digits" cannot drift into disagreeing (R31).
export const BADGE_MAX_DIGITS = 3;
export const BADGE_MAX = 10 ** BADGE_MAX_DIGITS - 1;
export function headerBadgeText(unread) {
  if (typeof unread !== "number" || !Number.isSafeInteger(unread) || unread <= 0) return "";
  return unread > BADGE_MAX ? `${BADGE_MAX}+` : String(unread);
}

// THE ACCESSIBLE NAME IS THE WHOLE TEXT OF THIS CHROME (14-B5: the half nobody looking at the screen
// can check), so which KEY a state resolves to is a law rather than a ternary at the render site.
// An unrostered kind answers "" rather than a plausible wrong name — a bell announced as "Messages"
// is worse than a bell announced as nothing, because a screen reader gives no way to notice it.
export function headerIconLabelKey(kind, unread) {
  if (!HEADER_ICON_KINDS.includes(kind)) return "";
  const unreadKey = kind === "messages" ? "nav.messagesUnread" : "nav.notificationsUnread";
  const restKey = kind === "messages" ? "nav.messages" : "nav.notifications";
  return headerBadgeText(unread) ? unreadKey : restKey;
}
